import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../../../utils/format';
import { api } from '@/services/api';

export default function EquipeTab() {
  const navigate = useNavigate();
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/personnel');
        setPersonnel(Array.isArray(res) ? res : (res.data || []));
      } catch (err) {
        console.error('Erreur lors du chargement du personnel:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const roleColors: Record<string, string> = {
    gerant: 'bg-foret-100 text-foret-700',
    responsable_atelier: 'bg-terre-100 text-terre-700',
    vendeur: 'bg-or-100 text-or-700',
    coupeur: 'bg-blue-100 text-blue-700',
    tailleur: 'bg-purple-100 text-purple-700',
  };
  
  const roleLabels: Record<string, string> = {
    gerant: 'Gérant',
    responsable_atelier: 'Resp. Atelier',
    vendeur: 'Vendeur',
    coupeur: 'Coupeur',
    tailleur: 'Tailleur',
  };

  const totalMasse = personnel.reduce((s, p) => s + (p.gains_mois || 0), 0);
  const currentMonth = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foret-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total employés', value: personnel.length, icon: 'ri-team-line', color: 'bg-foret-50 border-foret-200 text-foret-600' },
          { label: 'Paie ce mois', value: formatPrice(totalMasse), icon: 'ri-money-franc-circle-line', color: 'bg-or-50 border-or-200 text-or-600' },
          { label: 'Période', value: currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1), icon: 'ri-calendar-check-line', color: 'bg-terre-50 border-terre-200 text-terre-600' },
        ].map(k => (
          <div key={k.label} className={`rounded-2xl border p-4 ${k.color.split(' ').slice(0, 2).join(' ')}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${k.color.replace('bg-', 'bg-opacity-20 bg-')}`}>
               <i className={`${k.icon} ${k.color.split(' ')[2]} text-lg`} />
            </div>
            <p className="text-xl font-bold text-gray-900 leading-tight">{k.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Équipe */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-playfair font-bold text-base text-gray-900">Personnel actif</h3>
          <div className="flex gap-2">
            <button onClick={() => navigate('/parametres/personnel')} className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full font-semibold cursor-pointer hover:bg-gray-200 whitespace-nowrap">
              Gérer
            </button>
            <button onClick={() => navigate('/paie')} className="text-xs bg-foret-500 text-white px-3 py-1.5 rounded-full font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">
              Gestion paie
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {personnel.length === 0 ? (
             <p className="text-sm text-gray-400 py-4 col-span-full text-center">Aucun personnel actif trouvé.</p>
          ) : personnel.map(p => {
            const initiales = p.nom.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
            return (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-foret-50 transition-colors">
                <div className="w-10 h-10 rounded-full bg-foret-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {initiales}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{p.nom}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${roleColors[p.specialite] || 'bg-gray-100 text-gray-600'}`}>
                    {roleLabels[p.specialite] || p.specialite}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">{formatPrice(p.gains_mois || 0, false)}</p>
                  <p className="text-[10px] text-gray-400">{p.commandes_mois || 0} pièce{p.commandes_mois > 1 ? 's' : ''}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
